<template>
    <tailwind-layout :title="property.full_address + ' - maintenace request'">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden md:flex justify-between items-center"><!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">{{ property.full_address }}</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('my-property', { tab: 'maintenance' })">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="px-6 lg:px-8 mx-auto flex flex-col justify-center md:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex flex-col my-4">
                    <h1 class="font-semibold text-2xl leading-tight">{{ property.full_address }}</h1>
                    <div class="flex items-center space-x-4">
                        <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('my-property', { tab: 'maintenance' })">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                    </div>
                </div>
            </div>
        </template>
        <div class="shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="store" class="space-y-8 divide-y divide-gray-200">
                <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                Request Maintenance
                            </h3>
                        </div>
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 ">
                            <div class="sm:col-span-6">
                                <label class="block text-sm font-medium text-gray-700">
                                    Request Type <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-2">
                                    <div class="flex items-center mb-2">
                                        <input type="radio" v-model="form.request_type" value="non_emergency" id="non-emergency"
                                        class="h-4 w-4 text-indigo-600 focus:ring-rent-risk-blue-400 border-gray-300">
                                        <label for="non-emergency" class="ml-2 block text-sm text-gray-900">
                                            Non Emergency
                                        </label>
                                    </div>
                                    <div class="flex items-center">
                                        <input type="radio" v-model="form.request_type" value="emergency" id="emergency"
                                            class="h-4 w-4 text-indigo-600 focus:ring-rent-risk-blue-400 border-gray-300">
                                        <label for="emergency" class="ml-2 block text-sm text-gray-900">
                                            Emergency
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="form.request_type" class="sm:col-span-6">
                                <Listbox v-model="form.emergency_types_id" @change="updateSelectedEmergencyType">
                                    <div class="relative w-full sm:w-[50%]">
                                        <ListboxLabel class="block text-sm font-medium text-gray-700">Select Request <span class="text-red-700">*</span></ListboxLabel>
                                        <div class="relative mt-2">
                                            <ListboxButton class="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
                                                          :class="form.errors.emergency_types_id ? 'outline-red-300 focus:outline-red-500' : 'outline-gray-300 focus:outline-rent-risk-blue-400'">
                                                <div class="flex items-center justify-between w-full">
                                                    <div class="flex flex-col">
                                                        <span v-if="selectedOption">
                                                            <span class="text-sm font-medium text-gray-700 mr-2">{{ selectedOption.name }}</span>
                                                            <span class="text-xs text-gray-700">{{ selectedOption.description }}</span>
                                                        </span>
                                                        <span class="text-sm text-gray-700" v-else>Please select a Request Type</span>
                                                    </div>
                                                    <div class="text-gray-700 w-[20px] h-[20px]">
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" data-slot="icon" class="el ey om aex afc bas cpu"><path fill-rule="evenodd" d="M5.22 10.22a.75.75 0 0 1 1.06 0L8 11.94l1.72-1.72a.75.75 0 1 1 1.06 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 0 1 0-1.06ZM10.78 5.78a.75.75 0 0 1-1.06 0L8 4.06 6.28 5.78a.75.75 0 0 1-1.06-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1 0 1.06Z" clip-rule="evenodd"></path></svg>
                                                    </div>
                                                </div>
                                            </ListboxButton>

                                            <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                                    <ListboxOption as="template" v-for="emergencyType in filteredEmergencyTypes" :key="emergencyType.id" :value="emergencyType.id" v-slot="{ active, selected }">
                                                        <li class="flex" :class="[active ? 'bg-rent-risk-blue-400 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                                                            <div class="flex flex-col md:flex-row justify-between">
                                                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'w-full md:w-auto']">{{ emergencyType.name }}</span>
                                                                <span :class="[active ? 'text-indigo-200' : 'text-gray-500', 'ml-0 md:ml-2 mt-1 md:mt-0']">{{ emergencyType.description }}</span>
                                                            </div>

                                                            <span v-if="selected" :class="[active ? 'text-rent-risk-blue-400' : 'text-rent-risk-blue-400', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                                <font-awesome-icon :icon="faCheck" class="size-5" aria-hidden="true" />
                                                            </span>
                                                        </li>
                                                    </ListboxOption>
                                                </ListboxOptions>
                                            </transition>
                                        </div>
                                    </div>
                                </Listbox>
                                <p v-text="form.errors.emergency_types_id" class="text-red-500 text-sm"></p>
                            </div>

                            <!-- Show textarea only when "Other" is selected -->
                            <div class="sm:col-span-6">
                                <label for="other_description" class="block text-sm font-medium text-gray-700">
                                    Describe the Issue <span
                                    v-if="form.emergency_types_id && selectedOption && selectedOption.name === 'Other'"
                                    class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <textarea id="other_description" v-model="form.other_description" rows="10"

                                        v-bind:class="(form.errors.other_description)?'w-full sm:w-[50%] shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'w-full sm:w-[50%] shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                        placeholder="Please describe the issue in detail">
                                    </textarea>
                                    <p v-text="form.errors.other_description" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <label for="attachements" v-bind:class="(form.errors.attachements)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Attachment
                                </label>
                                <div class="mt-1">
                                    <input type="file" multiple @input="form.attachements = $event.target.files" id="attachements"
                                        :class="form.errors.attachements ? 'border-red-300' : 'border-gray-300'"
                                        class="w-full sm:w-[50%] shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                    <p class="text-gray-500 text-xs mt-1">To select more than one file hold Ctrl+Shift</p>
                                    <div v-if="form.errors" class="mt-1">
                                        <div v-for="(error, key, index) in form.errors" :key="key">
                                            <p v-if="key.startsWith('attachements.')" class="text-red-500 text-sm">
                                                {{ error.replace(key, '') }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('my-property', { tab: 'maintenance' })" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">Cancel</inertia-link>
                        <form-button :form="form" type="submit" class="ml-3">
                            Submit
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </tailwind-layout>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import FormButton from '@/Shared/FormButton.vue';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faChevronLeft, faCheck, faChevronDown } from "@fortawesome/pro-solid-svg-icons";


export default {
    props: {
        property: Object,
        emergencyTypes: Array,
        errors: null,
    },
    components: {
        TailwindLayout,
        FormButton,
        FontAwesomeIcon,
        Listbox,
        ListboxButton,
        ListboxLabel,
        ListboxOption,
        ListboxOptions
    },
    data() {
        return {
            faChevronLeft,
            faCheck,
            faChevronDown,
            form: this.$inertia.form({
                property_id: this.property.id,
                request_type: 'non_emergency',
                emergency_types_id: '',
                other_description: '',
                attachements: null
            }),
            dropdownOpenEmergency: false,
            selectedEmergencyType: null
        }
    },
    computed: {
        validationErrors(){
            let errors = Object.values(this.errors);
            errors = errors.flat();
            return errors;
        },
        filteredEmergencyTypes() {
            if (!this.form.request_type) return [];
            return this.emergencyTypes.filter(type => type.type === this.form.request_type);
        },
        selectedOption() {
            if (!this.form.emergency_types_id) return null;

            // Find the selected option from filteredEmergencyTypes
            return this.filteredEmergencyTypes.find(et => et.id === this.form.emergency_types_id) || null;
        }
    },
    methods: {
        store() {
            this.form.post(this.route('maintenance-requests.store', this.property.id), {
                preserveState: true,
                onSuccess: () => {
                    console.log('success');
                },
                onError: (errors) => {
                    console.error("Form Errors:", errors);
                }
            });
        },
        toggleEmergencyDropdown() {
            this.dropdownOpenEmergency = !this.dropdownOpenEmergency;
        },
        selectEmergencyType(emergencyType) {
            this.form.emergency_types_id = emergencyType.id;
            this.selectedEmergencyType = emergencyType;
            this.dropdownOpenEmergency = false;
        },
        updateSelectedEmergencyType() {
            this.selectedEmergencyType = this.filteredEmergencyTypes.find(et => et.id === this.form.emergency_types_id) || null;
        },
    }
}
</script>
